import React, { useRef, useState, useEffect } from 'react';
import { useAuth } from '../contexts/auth-context';
import { useOrganization } from '../contexts/organization-context';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchOrganizerById, createOrganizer, updateOrganizer, deleteOrganizerAvatar, deleteOrganizerBanner, addAuthorizationEmail, updateAuthorizationEmail, removeAuthorizationEmail, fetchOrganizerOwnEvents } from '../apiEndpoints';
import { Input, Textarea } from '@heroui/input';
import { addToast } from '@heroui/toast';
import { GoogleMap, useJsApiLoader, Autocomplete, Marker } from '@react-google-maps/api';
import moment from 'moment';
import Instagram from '@/icons/Instagram';
import Twitter from '@/icons/Twitter';
import Linkedin from '@/icons/Linkedin';
import Facebook from '@/icons/Facebook';
import Avatar from './Avatar';
import DeleteConfirmModal from './DeleteConfirmModal';
import EditIcon from '@/icons/EditIcon';
import Share from '@/icons/Share';
import {
    Section,
    AboutSectionIcon,
    VisualsSectionIcon,
    LinksSectionIcon,
    AccessSectionIcon,
    EmbedSectionIcon,
} from './OrganizerProfileSections';
import { card, btnPrimary, btnGhost, label as labelCls } from '@/components/ui/design';
import { Select, SelectItem } from '@/components/ui/AppSelect';

const heroInputClassNames = {
    inputWrapper: 'h-14 rounded-2xl bg-grey99 shadow-none ring-1 ring-black/[0.04] data-[hover=true]:bg-grey99 group-data-[focus=true]:bg-grey99 group-data-[focus=true]:ring-2 group-data-[focus=true]:ring-primary/50 dark:bg-white/[0.06] dark:ring-white/10 dark:data-[hover=true]:bg-white/[0.08] dark:group-data-[focus=true]:bg-white/[0.06] dark:group-data-[focus=true]:ring-nightAccent/60',
    input: 'text-base text-grey10 placeholder:text-grey60 dark:text-white dark:placeholder:text-nightMuted',
};
const heroTextareaClassNames = {
    inputWrapper: 'rounded-2xl bg-grey99 shadow-none ring-1 ring-black/[0.04] data-[hover=true]:bg-grey99 group-data-[focus=true]:bg-grey99 group-data-[focus=true]:ring-2 group-data-[focus=true]:ring-primary/50 dark:bg-white/[0.06] dark:ring-white/10 dark:data-[hover=true]:bg-white/[0.08] dark:group-data-[focus=true]:bg-white/[0.06] dark:group-data-[focus=true]:ring-nightAccent/60',
    input: 'text-base text-grey10 placeholder:text-grey60 dark:text-white dark:placeholder:text-nightMuted',
};
const codeAreaCls = 'flex-1 min-w-0 px-4 py-3 rounded-2xl bg-grey99 ring-1 ring-black/[0.04] text-grey10 dark:bg-white/[0.06] dark:ring-white/10 dark:text-nightTextSecondary font-mono text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary/50 dark:focus:ring-nightAccent/60';
const copyBtnCls = 'inline-flex h-12 shrink-0 self-start items-center justify-center rounded-full bg-grey10 px-5 text-sm font-semibold text-white whitespace-nowrap transition-colors hover:bg-black dark:bg-white dark:text-nightBg dark:hover:bg-grey90';
const smallPill = 'inline-flex h-9 items-center justify-center rounded-full px-4 text-sm font-semibold transition-colors disabled:opacity-50';
const sectionFooterCls = 'flex justify-end gap-2 pt-5 border-t border-black/[0.06] dark:border-white/10';

export default function OrganizerTab() {
    const { user } = useAuth();
    const userId = user?.userId;
    const queryClient = useQueryClient();
    const { selectedOrganizerId: contextSelectedOrganizerId, setSelectedOrganizerId: setContextSelectedOrganizerId } = useOrganization();

    // Local state for 'new' organization option (specific to this tab)
    const [localSelectedOrganizerId, setLocalSelectedOrganizerId] = useState<string | null>(null);
    // Use local if set to 'new', otherwise use context
    const selectedOrganizerId = localSelectedOrganizerId === 'new' ? 'new' : (localSelectedOrganizerId || contextSelectedOrganizerId);

    const [avatar, setAvatar] = useState<string | null>(null);
    const [avatarFile, setAvatarFile] = useState<File | null>(null);
    const [banner, setBanner] = useState<string | null>(null);
    const [bannerFile, setBannerFile] = useState<File | null>(null);
    const [orgName, setOrgName] = useState('');
    const [profileUrl, setProfileUrl] = useState('');
    const [about, setAbout] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [address, setAddress] = useState('');
    const [instagram, setInstagram] = useState('');
    const [twitter, setTwitter] = useState('');
    const [linkedin, setLinkedin] = useState('');
    const [facebook, setFacebook] = useState('');
    const [error, setError] = useState('');
    const [touched, setTouched] = useState<{ [k: string]: boolean }>({});

    // Collapsible sections
    const [expanded, setExpanded] = useState<{ [key: string]: boolean }>({ about: true });
    const [editingName, setEditingName] = useState(false);
    // Which section is currently being saved, so it only collapses once the save actually succeeds.
    const [pendingSection, setPendingSection] = useState<string | null>(null);
    const [copied, setCopied] = useState(false);
    function toggleSection(key: string) {
        setExpanded(prev => ({ ...prev, [key]: !prev[key] }));
    }

    // Authorization email management state
    const [newEmail, setNewEmail] = useState('');
    const [newRole, setNewRole] = useState<'admin' | 'eventmanager' | 'usher'>('usher');
    const [editingEmail, setEditingEmail] = useState<string | null>(null);
    const [editingRole, setEditingRole] = useState<'admin' | 'eventmanager' | 'usher'>('usher');
    const [removingEmails, setRemovingEmails] = useState<Set<string>>(new Set());
    const [emailToRemove, setEmailToRemove] = useState<string | null>(null);
    const [removingAvatar, setRemovingAvatar] = useState(false);
    const [removingBanner, setRemovingBanner] = useState(false);

    // Iframe embed state
    const [activeIframeTab, setActiveIframeTab] = useState<'events' | 'event' | 'members'>('events');
    const [selectedEventId, setSelectedEventId] = useState<string>('');


    // Google Maps state
    const [lat, setLat] = useState(34.0522);
    const [lng, setLng] = useState(-118.2437);
    const [autocomplete, setAutocomplete] = useState<any>(null);
    const [map, setMap] = useState<any>(null);
    const [addressLoading, setAddressLoading] = useState(false);
    const [lastFetchedCoords, setLastFetchedCoords] = useState<{ lat: number; lng: number } | null>(null);
    // Debounce timer
    const [debounceTimer, setDebounceTimer] = useState<NodeJS.Timeout | null>(null);
    const mapContainerStyle = { width: '100%', height: '220px', borderRadius: '16px' };

    const fileInputRef = useRef<HTMLInputElement>(null);
    const bannerInputRef = useRef<HTMLInputElement>(null);

    // Google Maps configuration
    const libraries: ("places")[] = ['places'];
    const { isLoaded } = useJsApiLoader({
        googleMapsApiKey: 'AIzaSyBKbmD1FAPDpYvIFHjUJpeZ9XbohnH_QFI',
        libraries: ['places'],
    });

    // Fetch organizer profile for selected organizer
    const { data: orgProfile, isLoading } = useQuery<any>({
        queryKey: ['organizerProfile', selectedOrganizerId],
        queryFn: async () => {
            if (!selectedOrganizerId) throw new Error('No organizer selected');
            return await fetchOrganizerById(selectedOrganizerId);
        },
        enabled: !!selectedOrganizerId,
        refetchOnWindowFocus: false,
    });

    // Fetch events for the selected organizer (for event iframe tab)
    const { data: organizerEvents, isLoading: isLoadingEvents } = useQuery<any>({
        queryKey: ['organizerEvents', selectedOrganizerId],
        queryFn: async () => {
            if (!selectedOrganizerId || selectedOrganizerId === 'new') throw new Error('No organizer selected');
            return await fetchOrganizerOwnEvents(selectedOrganizerId);
        },
        enabled: !!selectedOrganizerId && selectedOrganizerId !== 'new',
    });


    const pendingAvatarFile = useRef<File | null>(null);
    const pendingBannerFile = useRef<File | null>(null);
    pendingAvatarFile.current = avatarFile;
    pendingBannerFile.current = bannerFile;

    // Populate form fields when orgProfile loads
    useEffect(() => {
        if (orgProfile) {
            if (!pendingAvatarFile.current) {
                setAvatar(
                    orgProfile.attachments?.avatar?.link ||
                    orgProfile.avatar ||
                    ''
                );
            }
            if (!pendingBannerFile.current) {
                setBanner(
                    orgProfile.attachments?.banner?.link ||
                    orgProfile.banner ||
                    ''
                );
            }
            console.log('orgProfile', orgProfile);

            setOrgName(orgProfile.orgName || '');
            setProfileUrl(orgProfile.profileUrl || '');
            setAbout(orgProfile.about || '');
            setEmail(orgProfile.email || '');
            setPhone(orgProfile.phone || '');
            setAddress(orgProfile.address || '');
            setLat(orgProfile.lat ? parseFloat(orgProfile.lat) : 34.0522);
            setLng(orgProfile.lng ? parseFloat(orgProfile.lng) : -118.2437);
            setInstagram(orgProfile.instagram || '');
            setTwitter(orgProfile.twitter || '');
            setLinkedin(orgProfile.linkedin || '');
            setFacebook(orgProfile.facebook || '');
        }
    }, [orgProfile]);

    // Initialize local state from context when context changes
    useEffect(() => {
        if (contextSelectedOrganizerId && !localSelectedOrganizerId) {
            setLocalSelectedOrganizerId(null); // Use context value
        }
    }, [contextSelectedOrganizerId]);

    function validate() {
        const errors: { [k: string]: string } = {};
        if (!orgName) errors.orgName = 'Organization name is required';
        if (!email) errors.email = 'Email is required';
        else if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) errors.email = 'Invalid email';
        return errors;
    }
    const errors = validate();

    // Save/Upsert organizer profile
    const mutation = useMutation({
        mutationFn: async () => {
            if (!selectedOrganizerId) throw new Error('No organization selected');
            const formData = new FormData();
            formData.append('orgName', orgName);
            formData.append('profileUrl', profileUrl);
            formData.append('about', about);
            formData.append('email', email);
            formData.append('phone', phone);
            formData.append('address', address);
            formData.append('lat', lat.toString());
            formData.append('lng', lng.toString());
            formData.append('instagram', instagram);
            formData.append('twitter', twitter);
            formData.append('linkedin', linkedin);
            formData.append('facebook', facebook);

            if (avatarFile) formData.append('avatar', avatarFile);
            if (bannerFile) formData.append('banner', bannerFile);

            // Use create endpoint for new organizers, update endpoint for existing ones
            if (selectedOrganizerId === 'new') {
                // For creating, we don't need to append userId as it's extracted from the auth token
                return createOrganizer(formData);
            } else {
                // For updating, the organizer ID is extracted from the organization_id header
                // Make sure the organization_id is set in localStorage for the API interceptor
                localStorage.setItem('selectedOrganizerId', selectedOrganizerId);
                return updateOrganizer(formData);
            }
        },
        onSuccess: () => {
            addToast({
                title: 'Success',
                description: selectedOrganizerId === 'new' ? 'Organization created successfully!' : 'Profile updated!',
                color: 'success',
            });
            setError('');
            setAvatarFile(null);
            setBannerFile(null);
            queryClient.invalidateQueries({ queryKey: ['organizerProfile', selectedOrganizerId] });
            queryClient.invalidateQueries({ queryKey: ['accessibleOrganizers', user?.email] });
            queryClient.invalidateQueries({ queryKey: ['myOrganizations'] });
            queryClient.invalidateQueries({ queryKey: ['userOwnOrganizer', userId] });

            // If creating new organizer, switch to the newly created organizer
            if (selectedOrganizerId === 'new') {
                setLocalSelectedOrganizerId(null);
                setContextSelectedOrganizerId(userId || null);
            }

            // Only collapse the section that triggered this save, and only on success
            if (pendingSection) {
                setExpanded(prev => ({ ...prev, [pendingSection]: false }));
                setPendingSection(null);
            }
        },
        onError: (err: any) => {
            setError(err?.response?.data?.message || err?.message || 'Failed to save. Please try again.');
            setPendingSection(null);
        },
    });

    // Authorization email mutations
    const addEmailMutation = useMutation({
        mutationFn: () => addAuthorizationEmail(selectedOrganizerId!, newEmail, newRole),
        onSuccess: () => {
            setNewEmail('');
            setNewRole('usher');
            addToast({ title: 'Success', description: 'Authorization email added.', color: 'success' });
            setError('');
            queryClient.invalidateQueries({ queryKey: ['organizerProfile', selectedOrganizerId] });
            queryClient.invalidateQueries({ queryKey: ['organizerProfile', userId] });
            queryClient.invalidateQueries({ queryKey: ['accessibleOrganizers', user?.email] });
            queryClient.invalidateQueries({ queryKey: ['myOrganizations'] });
            queryClient.invalidateQueries({ queryKey: ['userOwnOrganizer', userId] });
        },
        onError: (err: any) => {
            setError(err?.message || 'Failed to add authorization email.');
        },
    });

    const updateEmailMutation = useMutation({
        mutationFn: () => {
            console.log('Updating authorization email:', { selectedOrganizerId, editingEmail, editingRole });
            return updateAuthorizationEmail(selectedOrganizerId!, editingEmail!, editingRole);
        },
        onSuccess: () => {
            console.log('Email updated successfully');
            setEditingEmail(null);
            setEditingRole('usher');
            addToast({ title: 'Success', description: 'Authorization email updated.', color: 'success' });
            setError('');
            queryClient.invalidateQueries({ queryKey: ['organizerProfile', selectedOrganizerId] });
            queryClient.invalidateQueries({ queryKey: ['organizerProfile', userId] });
            queryClient.invalidateQueries({ queryKey: ['accessibleOrganizers', user?.email] });
            queryClient.invalidateQueries({ queryKey: ['myOrganizations'] });
            queryClient.invalidateQueries({ queryKey: ['userOwnOrganizer', userId] });
        },
        onError: (err: any) => {
            console.error('Failed to update authorization email:', err);
            setError(err?.message || 'Failed to update authorization email.');
        },
    });

    const removeEmailMutation = useMutation({
        mutationFn: (email: string) => {
            console.log('Removing authorization email:', { selectedOrganizerId, email });
            setRemovingEmails(prev => new Set(prev).add(email));
            return removeAuthorizationEmail(selectedOrganizerId!, email);
        },
        onSuccess: (_, email) => {
            console.log('Email removed successfully');
            setRemovingEmails(prev => {
                const newSet = new Set(prev);
                newSet.delete(email);
                return newSet;
            });
            addToast({ title: 'Success', description: 'Authorization email removed.', color: 'success' });
            setError('');
            queryClient.invalidateQueries({ queryKey: ['organizerProfile', selectedOrganizerId] });
            queryClient.invalidateQueries({ queryKey: ['organizerProfile', userId] });
            queryClient.invalidateQueries({ queryKey: ['accessibleOrganizers', user?.email] });
            queryClient.invalidateQueries({ queryKey: ['myOrganizations'] });
            queryClient.invalidateQueries({ queryKey: ['userOwnOrganizer', userId] });
        },
        onError: (err: any, email) => {
            console.error('Failed to remove authorization email:', err);
            setRemovingEmails(prev => {
                const newSet = new Set(prev);
                newSet.delete(email);
                return newSet;
            });
            setError(err?.message || 'Failed to remove authorization email.');
        },
    });

    // Saving is scoped per section, but the underlying mutation always submits the
    // whole profile (orgName/email are required regardless of which section triggered it).
    function attemptSectionSave(sectionKey: string) {
        setTouched({ orgName: true, email: true });
        if (Object.keys(errors).length > 0) {
            setExpanded(prev => ({ ...prev, about: true }));
            addToast({ title: 'Cannot save yet', description: 'Fill in the required organization name and email in the About section first.', color: 'danger' });
            return;
        }
        setPendingSection(sectionKey);
        mutation.mutate();
    }

    function resetAboutFields() {
        setOrgName(orgProfile?.orgName || '');
        setAbout(orgProfile?.about || '');
        setEmail(orgProfile?.email || '');
        setPhone(orgProfile?.phone || '');
        setAddress(orgProfile?.address || '');
        setLat(orgProfile?.lat ? parseFloat(orgProfile.lat) : 34.0522);
        setLng(orgProfile?.lng ? parseFloat(orgProfile.lng) : -118.2437);
        setTouched(t => ({ ...t, orgName: false, email: false }));
        setExpanded(prev => ({ ...prev, about: false }));
    }

    function resetVisualsFields() {
        setAvatar(orgProfile?.attachments?.avatar?.link || orgProfile?.avatar || '');
        setAvatarFile(null);
        setBanner(orgProfile?.attachments?.banner?.link || orgProfile?.banner || '');
        setBannerFile(null);
        setExpanded(prev => ({ ...prev, visuals: false }));
    }

    function resetLinksFields() {
        setProfileUrl(orgProfile?.profileUrl || '');
        setInstagram(orgProfile?.instagram || '');
        setTwitter(orgProfile?.twitter || '');
        setLinkedin(orgProfile?.linkedin || '');
        setFacebook(orgProfile?.facebook || '');
        setExpanded(prev => ({ ...prev, links: false }));
    }

    function handleAvatarChange(e: React.ChangeEvent<HTMLInputElement>) {
        const file = e.target.files?.[0];
        if (file) {
            setAvatarFile(file);
            setAvatar(URL.createObjectURL(file));
        }
    }
    function handleBannerChange(e: React.ChangeEvent<HTMLInputElement>) {
        const file = e.target.files?.[0];
        if (file) {
            setBannerFile(file);
            setBanner(URL.createObjectURL(file));
        }
    }
    function handleRemoveAvatar() {
        setRemovingAvatar(true);
        setAvatar(null);
        setAvatarFile(null);
        deleteOrganizerAvatar()
            .then(() => {
                queryClient.invalidateQueries({ queryKey: ['organizerProfile', selectedOrganizerId] });
                queryClient.invalidateQueries({ queryKey: ['accessibleOrganizers', user?.email] });
                queryClient.invalidateQueries({ queryKey: ['myOrganizations'] });
                setRemovingAvatar(false);
            })
            .catch((err) => {
                setError(err?.message || 'Failed to remove avatar.');
                setRemovingAvatar(false);
            });
    }
    function handleRemoveBanner() {
        setRemovingBanner(true);
        setBanner(null);
        setBannerFile(null);
        deleteOrganizerBanner()
            .then(() => {
                queryClient.invalidateQueries({ queryKey: ['organizerProfile', selectedOrganizerId] });
                queryClient.invalidateQueries({ queryKey: ['accessibleOrganizers', user?.email] });
                queryClient.invalidateQueries({ queryKey: ['myOrganizations'] });
                setRemovingBanner(false);
            })
            .catch((err) => {
                setError(err?.message || 'Failed to remove banner.');
                setRemovingBanner(false);
            });
    }

    // Google Maps event handlers
    function onLoadAutocomplete(ac: any) {
        setAutocomplete(ac);
    }


    function onPlaceChanged() {
        if (autocomplete) {
            const place = autocomplete.getPlace();
            if (place.geometry && place.geometry.location) {
                const newLat = place.geometry.location.lat();
                const newLng = place.geometry.location.lng();
                setLat(newLat);
                setLng(newLng);
                setAddress(place.formatted_address);
                if (map) {
                    map.panTo({ lat: newLat, lng: newLng });
                }
            }
        }
    }

    function onMapLoad(mapInstance: any) {
        setMap(mapInstance);
    }
    function extractAddressFromComponents(addressComponents: any[]): { country: string; state: string; city: string; zipcode: string; fullAddress: string } {
        if (!addressComponents || !Array.isArray(addressComponents)) {
            return { country: '', state: '', city: '', zipcode: '', fullAddress: '' };
        }

        const address: { country: string; state: string; city: string; zipcode: string; fullAddress: string } = {
            country: '',
            state: '',
            city: '',
            zipcode: '',
            fullAddress: ''
        };

        // Extract country
        const country = addressComponents.find(component =>
            component.types.includes('country')
        );
        if (country) address.country = country.long_name;

        // Extract state/province
        const state = addressComponents.find(component =>
            component.types.includes('administrative_area_level_1')
        );
        if (state) address.state = state.long_name;

        // Extract city
        const city = addressComponents.find(component =>
            component.types.includes('locality')
        );
        if (city) address.city = city.long_name;

        // Extract postal code
        const zipcode = addressComponents.find(component =>
            component.types.includes('postal_code')
        );
        if (zipcode) address.zipcode = zipcode.long_name;

        return address;
    }
    async function fetchAddressFromCoords(lat: number, lng: number) {
        setAddressLoading(true);
        fetch(
            `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=AIzaSyBTl2hQB_YKIYPeWLD2qJ0G08yTo7SErP8`
        )
            .then(response => response.json())
            .then(data => {
                if (data.results && data.results[0]) {
                    const result = data.results[0];
                    // Extract complete address from address components
                    console.log('result', result);

                    setAddress(result.formatted_address)

                }
            })
            .catch(() => {
                // Optionally handle error
            })
            .finally(() => {
                setAddressLoading(false);
            });
    }
    function handleMapIdle() {
        if (map) {
            const center = map.getCenter();
            const newLat = center.lat();
            const newLng = center.lng();
            // Only fetch if coordinates changed significantly (0.00001 deg threshold)
            if (!lastFetchedCoords || Math.abs(newLat - lastFetchedCoords.lat) > 0.00001 || Math.abs(newLng - lastFetchedCoords.lng) > 0.00001) {
                setLastFetchedCoords({ lat: newLat, lng: newLng });
                // Debounce fetch
                if (debounceTimer) clearTimeout(debounceTimer);
                const timer = setTimeout(() => {
                    fetchAddressFromCoords(newLat, newLng);
                }, 400); // 400ms debounce
                setDebounceTimer(timer);
            }
            setLat(newLat);
            setLng(newLng);
        }
    }

    // Public page URL, used for the top summary card and the Share action.
    // Prefers the organization's handle (profileUrl) once set, since that's what the
    // public detail page resolves; falls back to the raw id otherwise.
    const pageUrl = selectedOrganizerId && selectedOrganizerId !== 'new'
        ? `${window.location.origin}/organization/${profileUrl || selectedOrganizerId}`
        : '';

    async function handleShare() {
        if (!pageUrl) return;
        if (navigator.share) {
            try {
                await navigator.share({ title: orgName || 'Organization', url: pageUrl });
            } catch {
                // user cancelled or share failed; nothing to do
            }
        } else {
            try {
                await navigator.clipboard.writeText(pageUrl);
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
            } catch {
                // clipboard write failed; nothing to do
            }
        }
    }

    // Show skeleton loader when loading organizer data (but not for new organizers)
    const showLoading = isLoading && selectedOrganizerId && selectedOrganizerId !== 'new';

    // Show skeleton loader
    if (showLoading) {
        return (
            <div className="w-full flex justify-center">
                <div className={`${card} sm:p-8 p-5 w-full flex flex-col gap-8`}>
                    <div className="animate-pulse space-y-8">
                        {/* Header skeleton */}
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-2 gap-4 sm:gap-0">
                            <div className="flex items-center gap-4">
                                <div className="w-16 h-16 bg-black/[0.06] dark:bg-white/10 rounded-full"></div>
                                <div className="space-y-2">
                                    <div className="h-7 bg-black/[0.06] dark:bg-white/10 rounded-full w-64 max-w-full"></div>
                                    <div className="h-4 bg-black/[0.06] dark:bg-white/10 rounded-full w-96 max-w-full"></div>
                                </div>
                            </div>
                            <div className="flex gap-2">
                                <div className="h-11 bg-black/[0.06] dark:bg-white/10 rounded-full w-24"></div>
                                <div className="h-11 bg-black/[0.06] dark:bg-white/10 rounded-full w-32"></div>
                            </div>
                        </div>
                        {[1, 2, 3].map((i) => (
                            <div key={i} className="h-16 bg-black/[0.06] dark:bg-white/10 rounded-[20px]"></div>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="w-full flex justify-center">
            <div className="w-full flex flex-col gap-4">
                {selectedOrganizerId ? (
                    <>
                        {selectedOrganizerId === 'new' ? (
                            <div className={`${card} sm:p-8 p-5`}>
                                <div className="text-[clamp(28px,4vw,40px)] font-bold leading-[1] tracking-[-0.04em] text-grey10 dark:text-white mb-3">Create New Organization</div>
                                <div className="text-base text-grey40 dark:text-nightMuted">Fill out the sections below to create your organization profile.</div>
                            </div>
                        ) : (
                            <div className={`${card} sm:p-8 p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5`}>
                                <div className="flex items-center gap-4 min-w-0">
                                    <Avatar src={avatar} name={orgName} seed={selectedOrganizerId !== 'new' ? selectedOrganizerId : undefined} variant="org" size={64} />
                                    <div className="min-w-0">
                                        <div className="flex items-center gap-2">
                                            {editingName ? (
                                                <input
                                                    autoFocus
                                                    value={orgName}
                                                    onChange={e => setOrgName(e.target.value)}
                                                    onBlur={() => { setEditingName(false); if (orgName) mutation.mutate(); }}
                                                    onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
                                                    className="text-2xl sm:text-3xl font-bold tracking-[-0.03em] text-grey10 dark:text-white border-b-2 border-primary dark:border-nightAccent outline-none bg-transparent min-w-0"
                                                />
                                            ) : (
                                                <div className="text-2xl sm:text-3xl font-bold tracking-[-0.03em] text-grey10 dark:text-white truncate">{orgName || 'Organization profile'}</div>
                                            )}
                                            <button
                                                type="button"
                                                onClick={() => setEditingName(true)}
                                                className="w-8 h-8 rounded-full bg-grey99 ring-1 ring-black/[0.06] hover:bg-black/[0.04] dark:bg-white/[0.06] dark:ring-white/10 dark:hover:bg-white/10 flex items-center justify-center flex-shrink-0 transition-colors"
                                                aria-label="Edit organization name"
                                            >
                                                <EditIcon width={14} height={14} />
                                            </button>
                                        </div>
                                        <div className="mt-1 text-sm text-grey40 dark:text-nightMuted truncate">
                                            My Page URL: <a href={pageUrl} target="_blank" rel="noopener noreferrer" className="font-semibold text-primary dark:text-nightAccent hover:underline">{pageUrl}</a>
                                        </div>
                                        <div className="text-xs font-medium uppercase tracking-[0.14em] text-grey60 dark:text-nightMuted mt-2">
                                            Last updated: {orgProfile?.updatedAt ? moment(orgProfile.updatedAt).fromNow() : '—'}
                                        </div>
                                    </div>
                                </div>
                                <div className="flex gap-2 flex-shrink-0">
                                    <button
                                        type="button"
                                        onClick={handleShare}
                                        className={`${btnGhost} h-11 flex-1 sm:flex-none`}
                                    >
                                        {copied ? 'Copied!' : 'Share'} <Share width={16} height={16} />
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => window.open(pageUrl, '_blank')}
                                        className={`${btnPrimary} h-11 flex-1 sm:flex-none`}
                                    >
                                        View Page
                                    </button>
                                </div>
                            </div>
                        )}
                    </>
                ) : (
                    <div className="rounded-[28px] bg-grey99 dark:bg-white/[0.04] px-6 py-16 text-center">
                        <div className="text-[clamp(24px,3.5vw,36px)] font-bold tracking-[-0.04em] text-grey10 dark:text-white mb-3">Select an Organization</div>
                        <div className="text-base text-grey40 dark:text-nightMuted">Choose an organization from the dropdown above to edit its profile.</div>
                    </div>
                )}

                {selectedOrganizerId && (
                    <>
                        {error && <div className="text-red-500 dark:text-red-400 text-sm px-1">{error}</div>}

                        <Section
                            icon={<AboutSectionIcon />}
                            title="About"
                            subtitle="Bio, contact info & location"
                            isOpen={!!expanded.about}
                            onEdit={() => toggleSection('about')}
                        >
                            <div>
                                <label className={labelCls}>Organization name *</label>
                                <Input placeholder="Name" classNames={heroInputClassNames} value={orgName} onChange={e => setOrgName(e.target.value)} onBlur={() => setTouched(t => ({ ...t, orgName: true }))} />
                                {touched.orgName && errors.orgName && <div className="text-xs text-[#E53935] dark:text-red-400 mt-1">{errors.orgName}</div>}
                            </div>
                            <div>
                                <label className={labelCls}>About</label>
                                <Textarea
                                    classNames={heroTextareaClassNames}
                                    placeholder="Tell people about your organization..."
                                    value={about}
                                    onChange={e => setAbout(e.target.value)}
                                    rows={5}
                                />
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                <div>
                                    <label className={labelCls}>Email *</label>
                                    <Input placeholder="e.g. Name@gmail.com" classNames={heroInputClassNames} value={email} onChange={e => setEmail(e.target.value)} onBlur={() => setTouched(t => ({ ...t, email: true }))} />
                                    {touched.email && errors.email && <div className="text-xs text-[#E53935] dark:text-red-400 mt-1">{errors.email}</div>}
                                </div>
                                <div>
                                    <label className={labelCls}>Phone</label>
                                    <Input placeholder="e.g. (777) 777-7777" classNames={heroInputClassNames} value={phone} onChange={e => setPhone(e.target.value)} />
                                </div>
                            </div>
                            <div>
                                <label className={labelCls}>Organization Address: <span className="text-red-500 dark:text-red-400">*</span></label>
                                {isLoaded && (
                                    <div>
                                        <Autocomplete
                                            onLoad={onLoadAutocomplete}
                                            onPlaceChanged={onPlaceChanged}
                                            options={{
                                                componentRestrictions: { country: 'us' },
                                                types: ['establishment', 'geocode']
                                            }}
                                        >
                                            <Input
                                                classNames={{
                                                    inputWrapper: heroInputClassNames.inputWrapper,
                                                    input: heroInputClassNames.input,
                                                }}
                                                className="w-full mb-3"
                                                placeholder="Search for a place or address in the US"
                                                value={addressLoading ? 'Loading address...' : address}
                                                onChange={e => setAddress(w => e.target.value)}
                                                autoComplete="street-address"
                                            />
                                        </Autocomplete>
                                        <div style={{ position: 'relative' }} className="overflow-hidden rounded-2xl ring-1 ring-black/[0.06] dark:ring-white/10">
                                            <GoogleMap
                                                mapContainerStyle={mapContainerStyle}
                                                zoom={12}
                                                onLoad={onMapLoad}
                                                onIdle={handleMapIdle}
                                                center={{ lat, lng }}
                                            >
                                                <Marker
                                                    position={{ lat, lng }}
                                                    icon={{
                                                        url: '/marker.png',
                                                        anchor: new window.google.maps.Point(16, 32),
                                                    }}
                                                />
                                            </GoogleMap>
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div className={sectionFooterCls}>
                                <button type="button" onClick={resetAboutFields} className={`${btnGhost} h-11`}>Cancel</button>
                                <button type="button" onClick={() => attemptSectionSave('about')} disabled={mutation.isPending} className={`${btnPrimary} h-11`}>
                                    {mutation.isPending && pendingSection === 'about' ? 'Saving...' : (selectedOrganizerId === 'new' ? 'Create Organization' : 'Save')}
                                </button>
                            </div>
                        </Section>

                        <Section
                            icon={<VisualsSectionIcon />}
                            title="Visuals"
                            subtitle="Logo & cover photo"
                            isOpen={!!expanded.visuals}
                            onEdit={() => toggleSection('visuals')}
                        >
                            <div>
                                <div className={labelCls}>Profile image</div>
                                <div className="flex items-center gap-4 mb-4">
                                    <div className="relative">
                                        <Avatar src={avatar} name={orgName} seed={selectedOrganizerId !== 'new' ? selectedOrganizerId : undefined} variant="org" size={64} className="ring-4 ring-white dark:ring-nightBg" />
                                    </div>
                                    <div className="flex gap-2">
                                        <button type="button" className={`${smallPill} h-10 bg-grey10 text-white hover:bg-black dark:bg-white dark:text-nightBg dark:hover:bg-grey90`} onClick={() => fileInputRef.current?.click()}>Upload</button>
                                        <input type="file" accept="image/*" ref={fileInputRef} className="hidden" onChange={handleAvatarChange} />
                                        <button
                                            type="button"
                                            className={`${smallPill} h-10 text-grey10 ring-1 ring-grey90 hover:bg-grey99 dark:text-white dark:ring-white/15 dark:hover:bg-white/[0.06]`}
                                            onClick={handleRemoveAvatar}
                                            disabled={removingAvatar}
                                        >
                                            {removingAvatar ? 'Removing...' : 'Remove'}
                                        </button>
                                    </div>
                                </div>
                                <div className={labelCls}>Profile page banner photo <span className="text-xs font-normal text-grey40 dark:text-nightMuted">(Min 1024 × 464, Max 5MB)</span></div>
                                <div className="w-full h-[120px] sm:h-[180px] bg-grey99 dark:bg-white/[0.04] border border-dashed border-black/10 dark:border-white/15 rounded-[24px] overflow-hidden flex flex-col items-center justify-center text-center relative">
                                    {banner ? (
                                        <img src={banner} alt="Banner preview" className="absolute inset-0 w-full h-full object-cover rounded-[24px]" />
                                    ) : (
                                        <>
                                            <svg width="40" height="40" fill="none" viewBox="0 0 40 40" className="mb-2"><rect x="8" y="12" width="24" height="16" rx="3" stroke="#B0B0B0" strokeWidth="2" /><path d="M8 28l7-7a2 2 0 012.8 0l4.2 4.2a2 2 0 002.8 0L32 20" stroke="#B0B0B0" strokeWidth="2" strokeLinecap="round" /></svg>
                                            <div className="text-grey50 dark:text-nightMuted text-base font-semibold">Upload Image</div>
                                        </>
                                    )}
                                    <input type="file" accept="image/*" ref={bannerInputRef} className="absolute inset-0 opacity-0 cursor-pointer" onChange={handleBannerChange} />
                                    {banner && (
                                        <button
                                            type="button"
                                            className="absolute top-3 right-3 bg-white/90 dark:bg-nightBg/90 rounded-full px-3 py-1.5 text-xs font-semibold text-grey10 dark:text-white ring-1 ring-black/[0.06] dark:ring-white/10 disabled:opacity-50"
                                            onClick={handleRemoveBanner}
                                            disabled={removingBanner}
                                        >
                                            {removingBanner ? 'Removing...' : 'Remove'}
                                        </button>
                                    )}
                                </div>
                            </div>
                            <div className={sectionFooterCls}>
                                <button type="button" onClick={resetVisualsFields} className={`${btnGhost} h-11`}>Cancel</button>
                                <button type="button" onClick={() => attemptSectionSave('visuals')} disabled={mutation.isPending} className={`${btnPrimary} h-11`}>
                                    {mutation.isPending && pendingSection === 'visuals' ? 'Saving...' : 'Save'}
                                </button>
                            </div>
                        </Section>

                        <Section
                            icon={<LinksSectionIcon />}
                            title="Links"
                            subtitle="Website & social media"
                            isOpen={!!expanded.links}
                            onEdit={() => toggleSection('links')}
                        >
                            <div>
                                <label className={labelCls}>Organization handle</label>
                                <div className="flex h-14 items-center rounded-2xl bg-grey99 ring-1 ring-black/[0.04] dark:bg-white/[0.06] dark:ring-white/10 overflow-hidden focus-within:ring-2 focus-within:ring-primary/50 dark:focus-within:ring-nightAccent/60">
                                    <span className="h-full flex items-center pl-4 pr-1 text-sm text-grey40 dark:text-nightMuted whitespace-nowrap truncate max-w-[60%]">{window.location.host}/organization/</span>
                                    <input
                                        value={profileUrl}
                                        onChange={e => setProfileUrl(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                                        placeholder="your-organization"
                                        className="flex-1 h-full pr-4 text-base outline-none min-w-0 bg-transparent text-grey10 placeholder:text-grey60 dark:text-white dark:placeholder:text-nightMuted"
                                    />
                                </div>
                                <div className="text-xs text-grey40 dark:text-nightMuted mt-2">Lowercase letters, numbers and hyphens only. Must be unique — this is your organization's public page address.</div>
                            </div>
                            <div className="flex flex-col gap-3">
                                <div>
                                    <label className={labelCls}>Instagram</label>
                                    <div className="flex items-center gap-2">
                                        <span className="w-14 h-14 shrink-0 flex items-center justify-center bg-grey99 ring-1 ring-black/[0.04] dark:bg-white/[0.06] dark:ring-white/10 rounded-2xl"><Instagram /></span>
                                        <Input placeholder="Instagram.com/username" classNames={heroInputClassNames} value={instagram} onChange={e => setInstagram(e.target.value)} />
                                    </div>
                                </div>
                                <div>
                                    <label className={labelCls}>X (Twitter)</label>
                                    <div className="flex items-center gap-2">
                                        <span className="w-14 h-14 shrink-0 flex items-center justify-center bg-grey99 ring-1 ring-black/[0.04] dark:bg-white/[0.06] dark:ring-white/10 rounded-2xl"><Twitter /></span>
                                        <Input placeholder="X.com/username" classNames={heroInputClassNames} value={twitter} onChange={e => setTwitter(e.target.value)} />
                                    </div>
                                </div>
                                <div>
                                    <label className={labelCls}>Linkedin</label>
                                    <div className="flex items-center gap-2">
                                        <span className="w-14 h-14 shrink-0 flex items-center justify-center bg-grey99 ring-1 ring-black/[0.04] dark:bg-white/[0.06] dark:ring-white/10 rounded-2xl"><Linkedin /></span>
                                        <Input placeholder="Linkedin.com/in/username" classNames={heroInputClassNames} value={linkedin} onChange={e => setLinkedin(e.target.value)} />
                                    </div>
                                </div>
                                <div>
                                    <label className={labelCls}>Facebook</label>
                                    <div className="flex items-center gap-2">
                                        <span className="w-14 h-14 shrink-0 flex items-center justify-center bg-grey99 ring-1 ring-black/[0.04] dark:bg-white/[0.06] dark:ring-white/10 rounded-2xl"><Facebook /></span>
                                        <Input placeholder="Facebook.com/username" classNames={heroInputClassNames} value={facebook} onChange={e => setFacebook(e.target.value)} />
                                    </div>
                                </div>
                            </div>
                            <div className={sectionFooterCls}>
                                <button type="button" onClick={resetLinksFields} className={`${btnGhost} h-11`}>Cancel</button>
                                <button type="button" onClick={() => attemptSectionSave('links')} disabled={mutation.isPending} className={`${btnPrimary} h-11`}>
                                    {mutation.isPending && pendingSection === 'links' ? 'Saving...' : 'Save'}
                                </button>
                            </div>
                        </Section>

                        {selectedOrganizerId !== 'new' && (
                            <Section
                                icon={<AccessSectionIcon />}
                                title="Access"
                                subtitle="Team & authorization emails"
                                isOpen={!!expanded.access}
                                onEdit={() => toggleSection('access')}
                            >
                                <div className="rounded-[24px] bg-grey99 ring-1 ring-black/[0.04] dark:bg-white/[0.04] dark:ring-white/[0.06] p-4 sm:p-5">
                                    <div className="space-y-4">
                                        {/* Add new email */}
                                        <div className="space-y-3">
                                            <div className="text-sm font-semibold text-grey20 dark:text-nightTextSecondary">Add New Authorization Email</div>
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                                <Input
                                                    classNames={heroInputClassNames}
                                                    type="email"
                                                    placeholder="Enter email address"
                                                    value={newEmail}
                                                    onChange={(e) => setNewEmail(e.target.value)}
                                                    className="w-full"
                                                />
                                                <Select aria-label="Select" selectedKeys={[String(newRole ?? '')]} onChange={(e) => setNewRole(e.target.value as 'admin' | 'eventmanager' | 'usher')}>
                                                    <SelectItem key="usher">Usher</SelectItem>
                                                    <SelectItem key="eventmanager">Event Manager</SelectItem>
                                                    <SelectItem key="admin">Admin</SelectItem>
                                                </Select>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => addEmailMutation.mutate()}
                                                disabled={!newEmail || addEmailMutation.isPending}
                                                className={`${btnPrimary} h-11`}
                                            >
                                                {addEmailMutation.isPending ? 'Adding...' : 'Add Email'}
                                            </button>
                                        </div>

                                        {/* Existing authorization emails */}
                                        {orgProfile?.authorizationEmails?.length > 0 ? (
                                            <div className="space-y-3">
                                                <div className="text-sm font-semibold text-grey20 dark:text-nightTextSecondary">Authorized Emails</div>
                                                {orgProfile.authorizationEmails.map((auth: any, index: number) => (
                                                    <div key={index} className="flex items-center justify-between bg-white dark:bg-white/[0.04] ring-1 ring-black/[0.06] dark:ring-white/10 p-4 rounded-2xl">
                                                        {editingEmail === auth.email ? (
                                                            <div className="flex-1 space-y-2">
                                                                <div className="text-sm font-semibold text-grey10 dark:text-white break-all">{auth.email}</div>
                                                                <Select aria-label="Select" selectedKeys={[String(editingRole ?? '')]} onChange={(e) => setEditingRole(e.target.value as 'admin' | 'eventmanager' | 'usher')}>
                                                                    <SelectItem key="usher">Usher</SelectItem>
                                                                    <SelectItem key="eventmanager">Event Manager</SelectItem>
                                                                    <SelectItem key="admin">Admin</SelectItem>
                                                                </Select>
                                                                <div className="flex gap-2">
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => {
                                                                            if (!selectedOrganizerId) {
                                                                                setError('No organization selected');
                                                                                return;
                                                                            }
                                                                            if (!editingEmail) {
                                                                                setError('No email selected for editing');
                                                                                return;
                                                                            }
                                                                            updateEmailMutation.mutate();
                                                                        }}
                                                                        disabled={updateEmailMutation.isPending || !selectedOrganizerId || !editingEmail}
                                                                        className={`${smallPill} bg-grey10 text-white hover:bg-black dark:bg-white dark:text-nightBg dark:hover:bg-grey90`}
                                                                    >
                                                                        {updateEmailMutation.isPending ? 'Saving...' : 'Save'}
                                                                    </button>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => setEditingEmail(null)}
                                                                        className={`${smallPill} text-grey10 ring-1 ring-grey90 hover:bg-grey99 dark:text-white dark:ring-white/15 dark:hover:bg-white/[0.06]`}
                                                                    >
                                                                        Cancel
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <div className='flex flex-col sm:flex-row sm:items-center gap-3 flex-1 min-w-0'>
                                                                <div className="flex-1 min-w-0">
                                                                    <div className="text-sm font-semibold text-grey10 dark:text-white break-all">{auth.email}</div>
                                                                    <span className="mt-1.5 inline-flex rounded-full bg-black/[0.05] px-2.5 py-0.5 text-xs font-semibold capitalize text-grey30 dark:bg-white/10 dark:text-nightTextSecondary">{auth.role}</span>
                                                                </div>
                                                                <div className="flex gap-2 justify-end">
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => {
                                                                            setEditingEmail(auth.email);
                                                                            setEditingRole(auth.role);
                                                                        }}
                                                                        className={`${smallPill} text-grey10 ring-1 ring-grey90 hover:bg-grey99 dark:text-white dark:ring-white/15 dark:hover:bg-white/[0.06]`}
                                                                    >
                                                                        Edit
                                                                    </button>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => {
                                                                            if (!selectedOrganizerId) {
                                                                                setError('No organization selected');
                                                                                return;
                                                                            }
                                                                            if (!auth.email) {
                                                                                setError('No email to remove');
                                                                                return;
                                                                            }
                                                                            setEmailToRemove(auth.email);
                                                                        }}
                                                                        disabled={removingEmails.has(auth.email) || !selectedOrganizerId || !auth.email}
                                                                        className={`${smallPill} bg-red-500/10 text-red-600 hover:bg-red-500/15 dark:text-red-400`}
                                                                    >
                                                                        {removingEmails.has(auth.email) ? 'Removing...' : 'Remove'}
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <div className="text-sm text-grey40 dark:text-nightMuted">No authorized emails for this organization.</div>
                                        )}
                                    </div>
                                </div>
                                <div className={sectionFooterCls}>
                                    <button type="button" onClick={() => toggleSection('access')} className={`${btnGhost} h-11`}>Close</button>
                                </div>
                            </Section>
                        )}

                        {selectedOrganizerId !== 'new' && (
                            <Section
                                icon={<EmbedSectionIcon />}
                                title="Embed"
                                subtitle="Add this page to your website"
                                isOpen={!!expanded.embed}
                                onEdit={() => toggleSection('embed')}
                            >
                                <div className="text-base text-grey40 dark:text-nightMuted">
                                    Use the code below to embed your organization's content on your website.
                                </div>

                                {/* Tabs */}
                                <div className="inline-flex self-start max-w-full overflow-x-auto rounded-full bg-grey99 p-1 ring-1 ring-black/[0.04] dark:bg-white/[0.06] dark:ring-white/[0.06]">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setActiveIframeTab('events');
                                            setSelectedEventId('');
                                        }}
                                        className={`h-9 rounded-full px-4 text-sm font-semibold whitespace-nowrap transition-all ${activeIframeTab === 'events'
                                            ? 'bg-grey10 text-white shadow-sm dark:bg-white dark:text-nightBg'
                                            : 'text-grey40 hover:text-grey10 dark:text-nightMuted dark:hover:text-white'
                                            }`}
                                    >
                                        Events
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setActiveIframeTab('event')}
                                        className={`h-9 rounded-full px-4 text-sm font-semibold whitespace-nowrap transition-all ${activeIframeTab === 'event'
                                            ? 'bg-grey10 text-white shadow-sm dark:bg-white dark:text-nightBg'
                                            : 'text-grey40 hover:text-grey10 dark:text-nightMuted dark:hover:text-white'
                                            }`}
                                    >
                                        Event
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setActiveIframeTab('members');
                                            setSelectedEventId('');
                                        }}
                                        className={`h-9 rounded-full px-4 text-sm font-semibold whitespace-nowrap transition-all ${activeIframeTab === 'members'
                                            ? 'bg-grey10 text-white shadow-sm dark:bg-white dark:text-nightBg'
                                            : 'text-grey40 hover:text-grey10 dark:text-nightMuted dark:hover:text-white'
                                            }`}
                                    >
                                        Members
                                    </button>
                                </div>

                                {(() => {
                                    // Determine iframe URL based on active tab
                                    let iframeUrl = '';
                                    let containerId = '';

                                    if (activeIframeTab === 'events') {
                                        iframeUrl = `${window.location.origin}/iframe/events/${selectedOrganizerId}`;
                                        containerId = `eventup-iframe-events-${selectedOrganizerId}`;
                                    } else if (activeIframeTab === 'event') {
                                        if (!selectedEventId) {
                                            iframeUrl = '';
                                        } else {
                                            iframeUrl = `${window.location.origin}/iframe/event/${selectedEventId}`;
                                            containerId = `eventup-iframe-event-${selectedEventId}`;
                                        }
                                    } else if (activeIframeTab === 'members') {
                                        iframeUrl = `${window.location.origin}/iframe/members/${selectedOrganizerId}`;
                                        containerId = `eventup-iframe-members-${selectedOrganizerId}`;
                                    }

                                    // Only generate codes if we have a valid URL
                                    if (!iframeUrl) {
                                        return (
                                            <>
                                                {/* Event selector for event tab */}
                                                {activeIframeTab === 'event' && (
                                                    <div>
                                                        <label className={labelCls}>Select Event</label>
                                                        {isLoadingEvents ? (
                                                            <div className="text-sm text-grey40 dark:text-nightMuted">Loading events...</div>
                                                        ) : (
                                                            <Select className="mb-4" aria-label="Select" selectedKeys={[String(selectedEventId ?? '')]} onChange={(e) => setSelectedEventId(e.target.value)}>
                                                                <SelectItem key="">-- Select an event --</SelectItem>
                                                                {Array.isArray(organizerEvents?.data) && organizerEvents.data.map((event: any) => (
                                                                    <SelectItem key={String(event._id)}>
                                                                        {event.title || event.name || event._id}
                                                                    </SelectItem>
                                                                ))}
                                                            </Select>
                                                        )}
                                                        {organizerEvents?.data?.length === 0 && (
                                                            <div className="text-sm text-grey40 dark:text-nightMuted mb-4">No events found for this organization.</div>
                                                        )}
                                                    </div>
                                                )}
                                            </>
                                        );
                                    }

                                    const iframeCode = `<iframe src="${iframeUrl}" width="100%" style="border: 0; height: 100vh; min-height: 100vh;" allowfullscreen></iframe>`;
                                    const ampIframeCode = `<div style="position: relative; width: 100%; height: 100vh; min-height: 100vh;">
  <amp-iframe
    src="${iframeUrl}"
    width="100"
    height="100"
    layout="fill"
    frameborder="0"
    allowfullscreen
    sandbox="allow-scripts allow-same-origin allow-popups allow-forms allow-top-navigation-by-user-activation allow-modals"
    resizable>
    <amp-img
      placeholder
      layout="fill"
      src="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'%3E%3Crect width='100' height='100' fill='%23f0f0f0'/%3E%3Ctext x='50' y='50' text-anchor='middle' dominant-baseline='middle' fill='%23666' font-family='Arial, sans-serif' font-size='14'%3ELoading...%3C/text%3E%3C/svg%3E"
      alt="Loading iframe content">
    </amp-img>
    <div overflow style="background: #f0f0f0; padding: 10px; text-align: center;">
      <button style="background: #009CA6; color: white; border: none; padding: 8px 16px; border-radius: 4px; cursor: pointer;">See more</button>
    </div>
  </amp-iframe>
</div>

<!-- Important: amp-iframe only works on valid AMP pages. Ensure your page includes:
<script async src="https://cdn.ampproject.org/v0.js"></script>
and has the proper AMP HTML structure. -->`;
                                    const scriptIframeCode = `<div id="${containerId}" style="width: 100%; height: 100vh; min-height: 100vh;"></div>
<script>
  (function() {
    var container = document.getElementById('${containerId}');
    if (container) {
      var iframe = document.createElement('iframe');
      iframe.src = '${iframeUrl}';
      iframe.width = '100%';
      iframe.height = '100%';
      iframe.style.border = '0';
      iframe.style.minHeight = '100vh';
      iframe.setAttribute('allowfullscreen', '');
      container.appendChild(iframe);
    }
  })();
</script>`;

                                    return (
                                        <>
                                            {/* Event selector for event tab */}
                                            {activeIframeTab === 'event' && (
                                                <div>
                                                    <label className={labelCls}>Select Event</label>
                                                    {isLoadingEvents ? (
                                                        <div className="text-sm text-grey40 dark:text-nightMuted">Loading events...</div>
                                                    ) : (
                                                        <Select className="mb-4" aria-label="Select" selectedKeys={[String(selectedEventId ?? '')]} onChange={(e) => setSelectedEventId(e.target.value)}>
                                                            <SelectItem key="">-- Select an event --</SelectItem>
                                                            {Array.isArray(organizerEvents?.data) && organizerEvents.data.map((event: any) => (
                                                                <SelectItem key={String(event._id)}>
                                                                    {event.title || event.name || event._id}
                                                                </SelectItem>
                                                            ))}
                                                        </Select>
                                                    )}
                                                    {organizerEvents?.data?.length === 0 && (
                                                        <div className="text-sm text-grey40 dark:text-nightMuted mb-4">No events found for this organization.</div>
                                                    )}
                                                </div>
                                            )}

                                            {/* Iframe URL */}
                                            <>
                                                <div>
                                                    <label className={labelCls}>Iframe URL</label>
                                                    <div className="flex gap-2 items-center">
                                                        <Input
                                                            classNames={heroInputClassNames}
                                                            readOnly
                                                            value={iframeUrl}
                                                            className="flex-1 font-mono text-sm"
                                                        />
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                navigator.clipboard.writeText(iframeUrl);
                                                                addToast({ title: 'Copied', description: 'Copied to clipboard.', color: 'success' });
                                                            }}
                                                            className={copyBtnCls}
                                                        >
                                                            Copy
                                                        </button>
                                                    </div>
                                                </div>

                                                {/* Regular Iframe Code */}
                                                <div>
                                                    <label className={labelCls}>Regular Iframe Code</label>
                                                    <div className="flex gap-2">
                                                        <textarea
                                                            readOnly
                                                            value={iframeCode}
                                                            className={codeAreaCls}
                                                            rows={2}
                                                        />
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                navigator.clipboard.writeText(iframeCode);
                                                                addToast({ title: 'Copied', description: 'Copied to clipboard.', color: 'success' });
                                                            }}
                                                            className={copyBtnCls}
                                                        >
                                                            Copy
                                                        </button>
                                                    </div>
                                                </div>

                                                {/* AMP Iframe Code */}
                                                <div>
                                                    <label className={labelCls}>
                                                        AMP Iframe Code
                                                        <span className="ml-2 text-xs text-orange-600 dark:text-orange-400 font-normal">(Requires valid AMP page)</span>
                                                    </label>
                                                    <div className="mb-3 px-4 py-3 bg-yellow-50 dark:bg-yellow-900/20 ring-1 ring-yellow-200 dark:ring-yellow-800 rounded-2xl text-xs text-yellow-800 dark:text-yellow-300">
                                                        <strong>Note:</strong> amp-iframe only works on valid AMP pages. Your page must include the AMP runtime script and follow AMP HTML structure. For regular HTML pages, use the "Regular Iframe Code" instead.
                                                    </div>
                                                    <div className="flex gap-2">
                                                        <textarea
                                                            readOnly
                                                            value={ampIframeCode}
                                                            className={codeAreaCls}
                                                            rows={12}
                                                        />
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                navigator.clipboard.writeText(ampIframeCode);
                                                                addToast({ title: 'Copied', description: 'Copied to clipboard.', color: 'success' });
                                                            }}
                                                            className={copyBtnCls}
                                                        >
                                                            Copy
                                                        </button>
                                                    </div>
                                                </div>

                                                {/* Script Iframe Code */}
                                                <div>
                                                    <label className={labelCls}>Script Iframe Code</label>
                                                    <div className="flex gap-2">
                                                        <textarea
                                                            readOnly
                                                            value={scriptIframeCode}
                                                            className={codeAreaCls}
                                                            rows={8}
                                                        />
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                navigator.clipboard.writeText(scriptIframeCode);
                                                                addToast({ title: 'Copied', description: 'Copied to clipboard.', color: 'success' });
                                                            }}
                                                            className={copyBtnCls}
                                                        >
                                                            Copy
                                                        </button>
                                                    </div>
                                                </div>
                                            </>
                                        </>
                                    );
                                })()}
                                <div className={sectionFooterCls}>
                                    <button type="button" onClick={() => toggleSection('embed')} className={`${btnGhost} h-11`}>Close</button>
                                </div>
                            </Section>
                        )}

                    </>
                )}
            </div>
            <DeleteConfirmModal
                isOpen={emailToRemove !== null}
                onClose={() => setEmailToRemove(null)}
                onConfirm={() => {
                    if (emailToRemove) removeEmailMutation.mutate(emailToRemove);
                    setEmailToRemove(null);
                }}
                title="Remove access"
                message="This person will lose access to this organization right away. You can add them again later."
                itemName={emailToRemove || undefined}
                confirmLabel="Remove"
            />
        </div>
    );
}
